import { fleetApiHttp } from '@/services/api/tattle-http'
import type { FleetJobHealth } from './claude-fleet.job-health'
import type { FleetJobAlerts, FleetJobAlertsChange } from './claude-fleet.job-alerts.types'
import type {
  FleetAuditEntry,
  FleetBrowserControlResult,
  FleetBrowserLiveView,
  FleetCommandReply,
  FleetConfigRecord,
  FleetConfigResponse,
  FleetDeadLetterDiscardResult,
  FleetDeadLetterRetryResult,
  FleetDeadLetters,
  FleetExplainRunResult,
  FleetHealth,
  FleetInsights,
  FleetQueueName,
  FleetRunNotifyResult,
  FleetRunGradeResult,
  FleetRunGradeValue,
  FleetSettingsHistoryEntry,
  FleetStopRunResult,
  FleetRunSteps,
  FleetTranscriptLink,
  FleetLoop,
  FleetLoopctlChange,
  FleetLoopctlChangeReply,
  FleetLoopctlPauses,
  FleetLoopState,
  FleetRunDetail,
  FleetRunnerActionRequest,
  FleetRunners,
  FleetRunsPage,
  FleetRunsQuery,
  FleetSettings,
  FleetStartRunRequest,
  FleetStartRunResult,
  FleetSummary,
} from './claude-fleet.types'
import type { FleetRunnerWindow } from './claude-fleet.runner-live.types'
import type { FleetRunTargets } from './claude-fleet.run-on'
import type { FleetFailures, FleetLaneMove, FleetLaneQueue, FleetLaneSearchResult, FleetRunnerDetail, FleetUpcoming, FleetWaiting, FleetWaitingCancelResult, FleetWaitingStartNowResult } from './claude-fleet.queue.types'
import type { FleetStagingQa, FleetStagingQaAction, FleetStagingQaActionResult } from './claude-fleet.staging-qa'
import type { FleetReleaseCheckResult } from './claude-fleet.release-check'
import type { NeedsPersonLane, NeedsPersonReply, NeedsPersonRequest } from './claude-fleet.needs-person'
import type { MyBuilds, Pipelines } from './claude-fleet.pipelines.types'
import type { FleetRunHandBackEligibility, FleetRunHandBackReply } from './claude-fleet.hand-back'
import type { FleetRunPendingReports } from './claude-fleet.run-reports'
import type { FleetHeadsUp, FleetHeadsUpMode, FleetRequestMove, FleetRequestQueue, FleetRequestReply } from './claude-fleet.requests.types'
import type {
  FleetNotificationTestResult, FleetNotifications, FleetNotificationsChange, FleetPushSubscription
} from './claude-fleet.notifications.types'

const runPath = (runId: string) => `/runs/${encodeURIComponent(runId)}`
const queuePath = (queue: FleetQueueName) => `/queues/${encodeURIComponent(queue)}`
const requestPath = (id: string) => `/requests/${encodeURIComponent(id)}`

export const ClaudeFleetApi = {
  async listRuns(query: FleetRunsQuery = {}) {
    const response = await fleetApiHttp.get<FleetRunsPage>('/runs', { params: query })
    return response.data
  },

  async getRun(runId: string) {
    const response = await fleetApiHttp.get<FleetRunDetail>(runPath(runId))
    return response.data
  },

  async sayToRun(runId: string, text: string) {
    const response = await fleetApiHttp.post<FleetCommandReply>(`${runPath(runId)}/say`, { text })
    return response.data
  },

  /** The person's answer to the question the run is waiting on, named by its id so a stale answer is refused. */
  async answerRun(runId: string, askId: string, text: string) {
    const response = await fleetApiHttp.post<FleetCommandReply>(`${runPath(runId)}/answer`, { askId, text })
    return response.data
  },

  async pauseRun(runId: string) {
    const response = await fleetApiHttp.post<FleetCommandReply>(`${runPath(runId)}/pause`)
    return response.data
  },

  async resumeRun(runId: string, text?: string) {
    const response = await fleetApiHttp.post<FleetCommandReply>(
      `${runPath(runId)}/resume`,
      text ? { text } : {}
    )
    return response.data
  },

  async stopRun(runId: string) {
    const response = await fleetApiHttp.post<FleetStopRunResult>(`${runPath(runId)}/stop`)
    return response.data
  },

  async explainRun(runId: string) {
    const response = await fleetApiHttp.post<FleetExplainRunResult>(`${runPath(runId)}/explain`)
    return response.data
  },

  async setRunNotify(runId: string, on: boolean) {
    const response = await fleetApiHttp.post<FleetRunNotifyResult>(`${runPath(runId)}/notify`, { on })
    return response.data
  },

  /** The caller's own grade of a run; null takes it away. */
  async gradeRun(runId: string, grade: FleetRunGradeValue | null, why?: string) {
    const response = await fleetApiHttp.post<FleetRunGradeResult>(`${runPath(runId)}/grade`, why ? { grade, why } : { grade })
    return response.data
  },

  async getBrowserLiveView(runId: string) {
    const response = await fleetApiHttp.get<FleetBrowserLiveView>(`${runPath(runId)}/browser`)
    return response.data
  },

  async setBrowserControl(runId: string, take: boolean) {
    const response = await fleetApiHttp.post<FleetBrowserControlResult>(`${runPath(runId)}/browser/control`, { take })
    return response.data
  },

  async getRunSteps(runId: string) {
    const response = await fleetApiHttp.get<FleetRunSteps>(`${runPath(runId)}/steps`)
    return response.data
  },

  async getTranscriptLink(runId: string) {
    const response = await fleetApiHttp.get<FleetTranscriptLink>(`${runPath(runId)}/transcript`)
    return response.data
  },

  async listDeadLetters(queue: FleetQueueName) {
    const response = await fleetApiHttp.get<FleetDeadLetters>(`${queuePath(queue)}/dead-letters`)
    return response.data
  },

  /** With no message id, every dead letter of the queue goes back. */
  async retryDeadLetters(queue: FleetQueueName, messageId?: string) {
    const response = await fleetApiHttp.post<FleetDeadLetterRetryResult>(
      `${queuePath(queue)}/dead-letters/retry`,
      messageId ? { messageId } : {}
    )
    return response.data
  },

  /** Only the named messages, since a discard cannot be undone; the fleet never discards a whole queue. */
  async discardDeadLetters(queue: FleetQueueName, messageIds: string[]) {
    const response = await fleetApiHttp.post<FleetDeadLetterDiscardResult>(`${queuePath(queue)}/dead-letters/discard`, { messageIds })
    return response.data
  },

  async startRun(request: FleetStartRunRequest) {
    const response = await fleetApiHttp.post<FleetStartRunResult>('/runs', request)
    return response.data
  },

  async listLoops() {
    const response = await fleetApiHttp.get<FleetLoop[]>('/loops')
    return response.data
  },

  async setLoopState(name: string, state: FleetLoopState) {
    const response = await fleetApiHttp.post<FleetCommandReply>(
      `/loops/${encodeURIComponent(name)}/state`,
      { state }
    )
    return response.data
  },

  async listLoopctlPauses() {
    const response = await fleetApiHttp.get<FleetLoopctlPauses>('/loopctl')
    return response.data
  },

  async setLoopctl(change: FleetLoopctlChange) {
    const response = await fleetApiHttp.post<FleetLoopctlChangeReply>('/loopctl', change)
    return response.data
  },

  async getSummary() {
    const response = await fleetApiHttp.get<FleetSummary>('/summary')
    return response.data
  },

  async listRunners() {
    const response = await fleetApiHttp.get<FleetRunners>('/runners')
    return response.data
  },

  async getRunTargets() {
    const response = await fleetApiHttp.get<Partial<FleetRunTargets>>('/runners/targets')
    return response.data
  },

  async getRunner(machine: string) {
    const response = await fleetApiHttp.get<FleetRunnerDetail>(`/runners/${encodeURIComponent(machine)}`)
    return response.data
  },

  /** The machine's /runner window conversation; its owner and fleet operators only. */
  async getRunnerWindow(machine: string) {
    const response = await fleetApiHttp.get<FleetRunnerWindow>(`/runners/${encodeURIComponent(machine)}/window`)
    return response.data
  },

  async getFailures(days: number) {
    const response = await fleetApiHttp.get<FleetFailures>('/failures', { params: { days } })
    return response.data
  },

  async listWaiting() {
    const response = await fleetApiHttp.get<FleetWaiting>('/waiting')
    return response.data
  },

  /** Drops these waiting runners-only jobs for good (fleet operators only). */
  async cancelWaiting(keys: string[], byName?: string) {
    const response = await fleetApiHttp.post<FleetWaitingCancelResult>('/waiting/cancel', { keys, byName })
    return response.data
  },

  /** Sends these waiting QA verifications to AgentCore (fleet operators only). */
  async startWaitingNow(keys: string[], byName?: string) {
    const response = await fleetApiHttp.post<FleetWaitingStartNowResult>('/waiting/start-now', { keys, byName })
    return response.data
  },

  async getUpcoming() {
    const response = await fleetApiHttp.get<FleetUpcoming>('/upcoming')
    return response.data
  },

  async getLaneQueue() {
    const response = await fleetApiHttp.get<FleetLaneQueue>('/lane-queue')
    return response.data
  },

  /** Puts a ticket at a position (1 is the top), or `reset` returns it to the ranked order. */
  async moveLaneQueueTicket(move: { key: string; position?: number; from?: number; reset?: boolean }) {
    const response = await fleetApiHttp.post<{ moves: FleetLaneMove[] }>('/lane-queue/move', move)
    return response.data
  },

  /** Tickets matching a key or words of the summary, each with whether the lane could take it. */
  async searchLaneQueue(q: string) {
    const response = await fleetApiHttp.get<{ results: FleetLaneSearchResult[] }>('/lane-queue/search', { params: { q } })
    return response.data
  },

  /** Asks the lane for a ticket and puts it at `position` (default 1, the top). */
  async addLaneQueueTicket(add: { key: string; position?: number }) {
    const response = await fleetApiHttp.post<{ moves: FleetLaneMove[] }>('/lane-queue/add', add)
    return response.data
  },

  async getRunHandBack(runId: string) {
    const response = await fleetApiHttp.get<FleetRunHandBackEligibility>(`${runPath(runId)}/hand-back`)
    return response.data
  },

  async handBackRun(runId: string, mine: boolean) {
    const response = await fleetApiHttp.post<FleetRunHandBackReply>(`${runPath(runId)}/hand-back`, mine ? { mine: true } : {})
    return response.data
  },

  async getRunPendingReports(runId: string) {
    const response = await fleetApiHttp.get<FleetRunPendingReports>(`${runPath(runId)}/pending-reports`)
    return response.data
  },

  async closeRunPendingReports(runId: string) {
    const response = await fleetApiHttp.post<FleetRunPendingReports>(`${runPath(runId)}/pending-reports`, {})
    return response.data
  },

  async getRequest(id: string) {
    const response = await fleetApiHttp.get<FleetRequestReply>(requestPath(id))
    return response.data
  },

  async getRequestQueue() {
    const response = await fleetApiHttp.get<FleetRequestQueue>('/queue')
    return response.data
  },

  async cancelRequest(id: string, reason?: string) {
    const response = await fleetApiHttp.post<FleetRequestReply>(`${requestPath(id)}/cancel`, reason ? { reason } : {})
    return response.data
  },

  async moveRequest(id: string, to: FleetRequestMove) {
    const response = await fleetApiHttp.post<FleetRequestReply>(`${requestPath(id)}/move`, { to })
    return response.data
  },

  async getNotifications() {
    const response = await fleetApiHttp.get<FleetNotifications>('/me/notifications')
    return response.data
  },

  async saveNotifications(change: FleetNotificationsChange) {
    const response = await fleetApiHttp.put<FleetNotifications>('/me/notifications', change)
    return response.data
  },

  async addNotificationBrowser(subscription: FleetPushSubscription, device: string) {
    const response = await fleetApiHttp.post<FleetNotifications>('/me/notifications/browsers', { subscription, device })
    return response.data
  },

  /** A POST, not a DELETE: the fleet's CORS does not allow DELETE. */
  async removeNotificationBrowser(id: string) {
    const response = await fleetApiHttp.post<FleetNotifications>(
      `/me/notifications/browsers/${encodeURIComponent(id)}`,
      { remove: true }
    )
    return response.data
  },

  async getJobAlerts() {
    const response = await fleetApiHttp.get<FleetJobAlerts>('/me/job-alerts')
    return response.data
  },

  async saveJobAlerts(change: FleetJobAlertsChange) {
    const response = await fleetApiHttp.put<FleetJobAlerts>('/me/job-alerts', change)
    return response.data
  },

  async sendTestNotification() {
    const response = await fleetApiHttp.post<FleetNotificationTestResult>('/me/notifications/test')
    return response.data
  },

  async getHeadsUp() {
    const response = await fleetApiHttp.get<FleetHeadsUp>('/me/heads-up')
    return response.data
  },

  async setHeadsUp(mode: FleetHeadsUpMode) {
    const response = await fleetApiHttp.put<FleetHeadsUp>('/me/heads-up', { mode })
    return response.data
  },

  async sendRunnerAction(machine: string, request: FleetRunnerActionRequest) {
    const response = await fleetApiHttp.post<FleetCommandReply>(
      `/runners/${encodeURIComponent(machine)}/actions`,
      request
    )
    return response.data
  },

  async listAudit() {
    const response = await fleetApiHttp.get<FleetAuditEntry[]>('/audit')
    return response.data
  },

  async getInsights() {
    const response = await fleetApiHttp.get<FleetInsights>('/insights')
    return response.data
  },

  async getReleaseStagingQa(version?: string) {
    const response = await fleetApiHttp.get<FleetStagingQa>('/release-staging-qa', { params: version ? { version } : {} })
    return response.data
  },

  async actOnReleaseStagingQa(action: FleetStagingQaAction, version: string) {
    const response = await fleetApiHttp.post<FleetStagingQaActionResult>(`/release-staging-qa/${action}`, { version })
    return response.data
  },

  async getReleaseNeedsPerson(version?: string) {
    const response = await fleetApiHttp.get<NeedsPersonLane>('/release/needs-person', { params: version ? { version } : {} })
    return response.data
  },

  async actOnReleaseNeedsPerson(request: NeedsPersonRequest) {
    const response = await fleetApiHttp.post<NeedsPersonReply>(`/release/needs-person/${request.action}`, request.body)
    return response.data
  },

  async checkRelease() {
    const response = await fleetApiHttp.post<FleetReleaseCheckResult>('/release-watch/check', {})
    return response.data
  },

  async getPipelines() {
    const response = await fleetApiHttp.get<Pipelines>('/pipelines')
    return response.data
  },

  async getMyBuilds() {
    const response = await fleetApiHttp.get<MyBuilds>('/me/builds')
    return response.data
  },

  async getHealth() {
    const response = await fleetApiHttp.get<FleetHealth>('/health')
    return response.data
  },

  async getJobHealth() {
    const response = await fleetApiHttp.get<FleetJobHealth>('/job-health')
    return response.data
  },

  async getConfigHistory() {
    const response = await fleetApiHttp.get<FleetSettingsHistoryEntry[]>('/config/history')
    return response.data
  },

  async getConfig() {
    const response = await fleetApiHttp.get<FleetConfigResponse>('/config')
    return response.data
  },

  async updateConfig(activity: string, settings: FleetSettings) {
    const response = await fleetApiHttp.post<FleetConfigRecord>(`/config/${encodeURIComponent(activity)}`, {
      settings
    })
    return response.data
  }
}
